import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage as T } from "react-intl";
import { Box, Button, Grid, Typography } from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { Theme, useTheme } from "@mui/material/styles";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import AddIcon from "@mui/icons-material/Add";
import produce from "immer";
import {
  DropTargetMonitor,
  DragSource,
  DropTarget,
  DropTargetConnector,
  DragSourceConnector,
  ConnectDropTarget,
  DragSourceMonitor,
  ConnectDragSource,
  ConnectDragPreview,
} from "react-dnd";
import { UseMutationResult } from "react-query";

import {
  Series,
  Module,
  Requirement,
  Response,
} from "services/KnowfullyAdminClient";
import { DragItem, DraggableInstance, shouldMove } from "utils/dndUtils";
import useBoolean from "hooks/useBoolean";
import { Mode } from "types/common-types"; 

import RequirementForm from "./RequirementForm";

type DragRequirementItem = DragItem & {
  moduleId: string;
};

type DraggableRequirementProps = {
  index: number;
  series: Series;
  module: Module;
  requirement: Requirement;
  onMoveRequirement: (fromIndex: number, toIndex: number) => void;
  onDropRequirement: () => void;
  onRequirementDeleted: () => void;
  onRequirementUpdated: () => void;
  mode: Mode;
  // Supplied by react-dnd HOCs
  isDragging: boolean;
  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
  connectDragPreview: ConnectDragPreview;
};

const useStyles = makeStyles((theme: Theme) => ({
  requirementsHeader: {
    display: "flex",
    justifyContent: "flex-end",
    margin: theme.spacing(4, 0, 3, 0),
  },
  editActions: {
    width: 100,
  },
  quantity: {
    width: 80,
  },
  dragHandle: {
    width: 30,
    display: "flex",
    alignItems: "center",
    cursor: "move",
  },
  ruleListHeader: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(1, 0),
  },
}));

const DraggableRequirement = forwardRef<
  HTMLDivElement,
  DraggableRequirementProps
>(
  (
    {
      series,
      module,
      requirement,
      onRequirementDeleted,
      onRequirementUpdated,

      isDragging,
      connectDragSource,
      connectDragPreview,
      connectDropTarget,
      mode,
    },
    ref
  ) => {
    const styles = useStyles(useTheme());
    const elementRef = useRef(null);
    connectDragSource(elementRef);
    connectDropTarget(elementRef);
    connectDragPreview(connectDropTarget(elementRef));

    useImperativeHandle<any, DraggableInstance>(ref, () => ({
      getNode: () => elementRef.current,
    }));

    const opacity = isDragging ? 0 : 1;
    const isUpdate = mode === Mode.Update;

    return (
      <Box mb={2}>
        <div
          ref={elementRef}
          style={{
            opacity,
            transform: "translate3d(0, 0, 0)",
            backgroundColor: "white",
          }}
        >
          <Box display="flex" alignItems="center" flex={1}>
            {isUpdate && connectDragSource(
              <div>
                <Box sx={styles.dragHandle}>
                  <DragHandleIcon />
                </Box>
              </div>
            )}
            <RequirementForm
              series={series}
              module={module}
              requirement={requirement}
              mode={mode}
              onRequirementDeleted={onRequirementDeleted}
              onRequirementUpdated={onRequirementUpdated}
            />
          </Box>
        </div>
      </Box>
    );
  }
);

const EnabledDraggableRequirement = DropTarget(
  "MODULE_REQUIREMENT",
  {
    drop({ onDropRequirement }: DraggableRequirementProps) {
      onDropRequirement();
    },
    hover(
      { index, requirement, onMoveRequirement }: DraggableRequirementProps,
      monitor: DropTargetMonitor,
      component: DraggableInstance
    ) {

      if (!component) {
        return null;
      }

      // node = HTML Div element from imperative API
      const node = component.getNode();
      if (!node) {
        return null;
      }

      const item = monitor.getItem<DragRequirementItem>();
      const dragIndex = item.index;
      const hoverIndex = index;

      if (
        item.moduleId === requirement.moduleId &&
        shouldMove(hoverIndex, node, dragIndex, monitor)
      ) {
        onMoveRequirement(item.index, index);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.index = hoverIndex;
      }
    },
  },
  (connect: DropTargetConnector) => ({
    connectDropTarget: connect.dropTarget(),
  })
)(
  DragSource(
    "MODULE_REQUIREMENT",
    {
      beginDrag: ({ requirement, index }: DraggableRequirementProps) => ({
        id: requirement.id,
        moduleId: requirement.moduleId,
        index,
      }),
    },
    (connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
      connectDragSource: connect.dragSource(),
      connectDragPreview: connect.dragPreview(),
      isDragging: monitor.isDragging(),
    })
  )(DraggableRequirement)
);

type OrderableModulesProps = {
  series: Series;
  mode: Mode;
  module: Module;
  onRequirementDeleted: () => void;
  onRequirementUpdated: () => void;
  reorderRequirementsMutation: UseMutationResult<
    Response,
    unknown,
    { moduleId: string; orderedRequirements: Requirement[] },
    unknown
  >;
};

const OrderableModuleRequirements = ({
  series,
  mode,
  module,
  onRequirementDeleted,
  onRequirementUpdated,
  reorderRequirementsMutation,
}: OrderableModulesProps) => {
  const styles = useStyles(useTheme());
  const [addingRequirement, addingRequirementActions] = useBoolean(false);
  const [orderedRequirements, setOrderedRequirements] = useState(
    module.requirements ?? []
  );
  const isUpdate = mode === Mode.Update;

  const onMoveRequirement = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragModule = orderedRequirements[dragIndex];
      const updatedRequirementsOrder = produce(orderedRequirements, (draft) => {
        draft.splice(dragIndex, 1);
        draft.splice(hoverIndex, 0, dragModule);
      });
      setOrderedRequirements(updatedRequirementsOrder);
    },
    [orderedRequirements]
  );

  const onDropRequirement = useCallback(
    () =>
      reorderRequirementsMutation.mutate({
        moduleId: module.id,
        orderedRequirements: orderedRequirements,
      }),
    [orderedRequirements, reorderRequirementsMutation, module.id]
  );

  useEffect(() => {
    setOrderedRequirements(module.requirements ?? []);
  }, [module.requirements]);

  const onRequirementAdded = () => {
    addingRequirementActions.setFalse();
    onRequirementUpdated();
  };

  return (
    <Box width={1} mx={2}>
      <Box sx={styles.requirementsHeader}>
        <Button
          onClick={addingRequirementActions.setTrue}
          disabled={!isUpdate}
          startIcon={<AddIcon />}
        >
          <T id="seriesAddRequirementButton" />
        </Button>
      </Box>

      <Box sx={styles.ruleListHeader}>
        <Box sx={styles.dragHandle} />
        <Grid container spacing={1}>
          <Grid item xs={5}>
            <Typography variant="subtitle1">
              <T id="productLabel" />
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <Typography variant="subtitle1">
              <T id="skuFormatLabel" />
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Box sx={styles.editActions} />
          </Grid>
        </Grid>
      </Box>

      {orderedRequirements.map((requirement, i) => {
        return (
          <EnabledDraggableRequirement
            key={requirement.id}
            series={series}
            module={module}
            requirement={requirement}
            mode={mode}
            index={i}
            onRequirementDeleted={onRequirementDeleted}
            onRequirementUpdated={onRequirementUpdated}
            onMoveRequirement={onMoveRequirement}
            onDropRequirement={onDropRequirement}
          />
        );
      })}

      {addingRequirement && (
        <Box display="flex" alignItems="center" flex={1}>
          <Box sx={styles.dragHandle} />
          <RequirementForm
            series={series}
            module={module}
            mode={mode}
            onRequirementDeleted={addingRequirementActions.setFalse}
            onRequirementUpdated={onRequirementAdded}
          />
        </Box>
      )}
    </Box>
  );
};

export default OrderableModuleRequirements;
